import { useCallback, useEffect, useState } from "react";
import "./Select.scss";

import SelectSprite from "./Img/select_sprite.svg";
import { WidthFormControl } from "../../../models/models";
import { Option } from "../../helpers/helpers";
import ImageSprite from "../ImageSprite/ImageSprite";

export enum TypeSelect {
    SEARCH = "search",
    SELECT = "select",
}

export type InputSearchProps = {
    placeholder: string | undefined;
    value: string | undefined;
    valueHandler: (value: string) => void;
    readOnly: boolean;
    type: TypeSelect;
    onAction: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const InputSearch = ({
    value,
    placeholder,
    valueHandler,
    readOnly = false,
    type,
    onAction,
}: InputSearchProps) => {
    return (
        <input
            type-cursor={type}
            readOnly={readOnly}
            value={value}
            type="text"
            className={`form_control__input ${type}`}
            placeholder={placeholder}
            onBlur={(e) => {
                onAction(e);
            }}
            onFocus={(e) => {
                onAction(e);
            }}
            onChange={(e) => {
                e.preventDefault();
                valueHandler(e.target.value);
            }}
        />
    );
};

export type OnChangeSelect = (currentId: number, currentName: string) => void;
export type OnResetSelectHandler = (
    currentId: number | undefined,
    currentName: string | undefined
) => void;

export interface SelectProps<T extends Option> {
    label?: string;
    options?: T[] | undefined;
    onChange?: OnChangeSelect;
    onReset?: OnResetSelectHandler;
    currentId: number | undefined;
    type?: "search" | "select";
    mixin?: string;
    widthFormControlWidth?: WidthFormControl;
    placeholder?: string;
    readOnly?: boolean;
}

const Select = <T extends Option>({
    options = [],
    onChange,
    onReset,
    label,
    currentId,
    widthFormControlWidth = WidthFormControl.NORMAL,
    type = TypeSelect.SELECT,
    placeholder,
    readOnly
}: SelectProps<T>) => {
    const isSelectType = type === TypeSelect.SELECT;
    const isSearchType = type === TypeSelect.SEARCH;

    const [optionId, setOptionId] = useState<number | undefined>(currentId);
    const [isElementFocused, setIsElementFocused] = useState(false);
    const [optionsSelect, setOptionsSelect] = useState(options);
    const [filterText, setFilterText] = useState("");

    const handleSelectChange = useCallback(
        (currentId: number, currentName: string) => {
            setOptionId(currentId);
            setFilterText(currentName);
            onChange?.(currentId, currentName);
        },
        [onChange]
    );

    useEffect(() => {
        if (optionId === undefined) return;

        // The id may be missing from the list (options not loaded yet, or the
        // entity references something the list does not contain) — leave the
        // caption as is instead of indexing with -1.
        const currentOption = options.find(
            (option) => String(option.id) === String(optionId)
        );
        if (!currentOption) return;

        setFilterText(currentOption.name);
        setOptionId(currentOption.id);
    }, [optionId, options]);

    const resetSelectHandler = useCallback((): void => {
        setIsElementFocused(false);
        setFilterText("");
        setOptionId(undefined);
        onReset?.(undefined, undefined);
    }, []);

    useEffect(() => {
        if (isSearchType && filterText.length === 0 && optionId !== undefined) {
            resetSelectHandler();
        }
    }, [filterText, resetSelectHandler, optionId, isSearchType]);

    useEffect(() => {
        if (isSearchType && options.length) {
            const filteredOptions = options.filter((option) =>
                option.name.toUpperCase().includes(filterText.toUpperCase())
            );
            setOptionsSelect(filteredOptions);
        }
    }, [filterText, isSearchType]);

    const handleFocusChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.type === "focus") {
                setIsElementFocused(true);
            } else if (event.type === "blur") {
                setIsElementFocused(false);
            }
        },
        []
    );

    const iconSrc = isSelectType ? "arrow" : "search";

    return (
        <div
            className={`form_control ${isElementFocused ? "focus" : ""}`}
            style={{ maxWidth: widthFormControlWidth }}
        >
            {label && <label className="label">{label}</label>}

            <div className="select">
                <div className="select__caption unSelectable">
                    <InputSearch
                        type={type as TypeSelect}
                        readOnly={readOnly ?? isSelectType}
                        value={filterText}
                        placeholder={isSearchType ? placeholder : ""}
                        valueHandler={setFilterText}
                        onAction={handleFocusChange}
                    />

                    {!readOnly && (
                        optionId !== undefined && isSearchType ? (
                            <button
                                className="select__caption_img"
                                type="button"
                                onClick={() => resetSelectHandler()}
                            >
                                <ImageSprite iconSprite={`${SelectSprite}#clean`} />
                            </button>
                        ) : (
                            <div className="select__caption_img">
                                <ImageSprite iconSprite={`${SelectSprite}#${iconSrc}`} />
                            </div>
                        )
                    )}

                </div>

                {!readOnly && optionsSelect.length > 0 && (
                    <div className="select__popup">
                        <div className="select__popup_container">
                            {optionsSelect.map((option) => (
                                <li className="select__option" key={option.id}>
                                    <button
                                        type="button"
                                        className={`select__option_button ${optionId === option.id ? "selected" : ""}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSelectChange(option.id, option.name);
                                        }}
                                    >
                                        <span>{option.name}</span>
                                    </button>
                                </li>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Select;